import { pickStateThatShouldTriggerSearchResultsToExecute } from './searchResults$';
import { mapSearchResults, type SearchResult } from './mapSearchResults';
import { reportReader$ } from './reportReader$';
import type { ReportSearchResults$State } from './types';
import {
  distinctUntilChanged,
  filter,
  from,
  map,
  startWith,
  switchMap,
} from 'rxjs';
import { isEqual } from 'lodash';
import {
  fetchPresentationSearchResults,
  keepValidReaderStateFilters,
} from './utils';
import { FetchPresentationSearchResultParams } from './types';
import { ArdoqId } from '@ardoq/api-types';

type PresentationSearchResult = SearchResult & {
  slideId: ArdoqId | null;
};

const toPresentationApiRequest = (
  state: ReportSearchResults$State
): FetchPresentationSearchResultParams => ({
  reportId: state.reportId!,
  slideId: state.slideId!,
  presentationId: state.presentationId!,
  filters: state.filters,
  sort: state.sort,
  pagination: state.pagination,
});

export const presentationSearchResults$ = reportReader$.pipe(
  map(keepValidReaderStateFilters),
  map(pickStateThatShouldTriggerSearchResultsToExecute),
  distinctUntilChanged<ReportSearchResults$State>(isEqual),
  filter(
    (
      a: ReportSearchResults$State
    ): a is ReportSearchResults$State & {
      reportId: ArdoqId;
      presentationId: ArdoqId;
      slideId: ArdoqId;
    } =>
      Boolean(
        a.reportId &&
        a.hasFinishedFetchReport &&
        !a.reportAPIError &&
        a.presentationId &&
        a.slideId
      )
  ),
  map(toPresentationApiRequest),
  switchMap(request =>
    from(fetchPresentationSearchResults(request)).pipe(
      map((response): PresentationSearchResult => ({
        ...mapSearchResults({
          searchResults: response,
          snapshot: null,
          pagination: request.pagination,
        }),
        slideId: request.slideId,
      }))
    )
  ),
  startWith<PresentationSearchResult>({
    apiError: null,
    searchResults: null,
    filterableColumns: [],
    filters: null,
    slideId: null,
  })
);
